import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, TextInput } from '@mantine/core';
import { Button as SharedButton } from '../../../../components/Button';
import {
  CreatedPrinterAgent,
  PrinterAgentView,
  printerAgentsService,
} from '../../../../services/printer-agents/printer-agents.service';
import classes from './shared/settings-shared.module.scss';

type PrinterAgentsSectionProps = {
  tenantId: string;
  employeeSession: string;
};

// Architectural summary: Manager-only settings panel for minting and revoking long-lived printer-agent bearer tokens. Mints via POST /tenants/:tenantId/printer-agents (plaintext token returned ONCE; backend stores only sha256 hash). Lists via GET, revokes via DELETE. The plaintext token is shown only on the create response — operator copies it into the desktop printer-agent EXE config to replace the daily cookie + x-employee-session ritual. Touching this UI affects how operators provision/rotate desktop print agents; do not log or persist plaintext tokens client-side.
export const PrinterAgentsSection: React.FC<PrinterAgentsSectionProps> = ({
  tenantId,
  employeeSession,
}) => {
  const [agents, setAgents] = useState<PrinterAgentView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdAgent, setCreatedAgent] = useState<CreatedPrinterAgent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const loadAgents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const list = await printerAgentsService.listAgents(tenantId, employeeSession);
      setAgents(list);
    } catch (err: any) {
      setError(err?.message || 'Failed loading printer agents');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId, employeeSession]);

  useEffect(() => {
    void loadAgents();
  }, [loadAgents]);

  const handleCreate = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Name is required');
        return;
      }
      setIsCreating(true);
      setError(null);
      try {
        const created = await printerAgentsService.createAgent(tenantId, employeeSession, trimmed);
        setCreatedAgent(created);
        setName('');
        await loadAgents();
      } catch (err: any) {
        setError(err?.message || 'Failed creating printer agent');
      } finally {
        setIsCreating(false);
      }
    },
    [name, tenantId, employeeSession, loadAgents],
  );

  const handleRevoke = useCallback(
    async (agentId: number) => {
      const confirmed = window.confirm('Revoke this agent token? The desktop EXE will stop printing until a new token is issued.');
      if (!confirmed) return;
      setError(null);
      try {
        await printerAgentsService.revokeAgent(tenantId, employeeSession, agentId);
        await loadAgents();
      } catch (err: any) {
        setError(err?.message || 'Failed revoking agent');
      }
    },
    [tenantId, employeeSession, loadAgents],
  );

  // Architectural summary: Copies the one-time token cross-browser. WebKit (Safari/iOS,
  // incl. iOS Chrome) rejects the async Clipboard API unless it runs synchronously in the
  // user gesture, so we fall back to a legacy execCommand select-and-copy. Surfaces a
  // "הועתק" confirmation only if copy succeeded.
  const handleCopyToken = useCallback(async () => {
    if (!createdAgent) return;
    const text = createdAgent.token;
    let success = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        success = true;
      } else {
        throw new Error('clipboard-api-unavailable');
      }
    } catch {
      // WebKit / older Safari / insecure context: legacy synchronous copy.
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      textarea.setSelectionRange(0, text.length); // iOS needs the explicit range
      try {
        success = document.execCommand('copy');
      } catch {
        // Last resort: token stays visible for manual copy.
      }
      document.body.removeChild(textarea);
    }
    if (success) {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      setCopied(true);
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, 2000);
    }
  }, [createdAgent]);

  return (
    <section data-testid="printer-agents-section" className={classes.section}>
      <p className={classes.lead}>
        צור טוקן ארוך-טווח לחיבור מדפסת שולחני. הדבק אותו במסך ההגדרות של ה-EXE — הוא
        מחליף את עוגיית ה-cookie היומית ואת סשן העובד.
      </p>

      <form onSubmit={handleCreate} className={classes.section}>
        <div className={classes.formRow}>
          <TextInput
            data-testid="printer-agents-name"
            label="שם הסוכן"
            placeholder="למשל ‘Kitchen PC’"
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            minLength={1}
            maxLength={80}
            required
            radius="md"
          />
        </div>
        <div className={classes.formActions}>
          <Button
            type="button"
            data-testid="printer-agents-refresh"
            variant="default"
            radius="md"
            onClick={() => void loadAgents()}
            disabled={isLoading}
          >
            {isLoading ? 'מרענן...' : 'רענן'}
          </Button>
          <SharedButton type="submit" variant="save" size="sm" disabled={isCreating} dataTestId="printer-agents-create">
            {isCreating ? 'יוצר...' : 'צור טוקן סוכן'}
          </SharedButton>
        </div>
      </form>

      {createdAgent && (
        <div data-testid="printer-agent-created-token" className={classes.tokenCard}>
          <p className={classes.tokenCardTitle}>העתק את הטוקן עכשיו — הוא לא יוצג שוב.</p>
          <code className={classes.tokenValue}>{createdAgent.token}</code>
          <div className={classes.formActions}>
            <Button
              variant={copied ? 'light' : 'default'}
              color={copied ? 'green' : undefined}
              radius="md"
              data-testid="printer-agents-copy"
              onClick={() => void handleCopyToken()}
            >
              {copied ? 'הועתק ✓' : 'העתק'}
            </Button>
            <Button variant="subtle" color="gray" radius="md" data-testid="printer-agents-close-token" onClick={() => setCreatedAgent(null)}>
              סגור
            </Button>
          </div>
        </div>
      )}

      {error && <div className={classes.error}>{error}</div>}

      <div className={classes.tableWrap}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>שם</th>
              <th>נוצר</th>
              <th>נראה לאחרונה</th>
              <th>סטטוס</th>
              <th>פעולות</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => {
              const revoked = Boolean(agent.revokedAt);
              return (
                <tr key={agent.id}>
                  <td>{agent.name}</td>
                  <td>{new Date(agent.createdAt).toLocaleString()}</td>
                  <td>{agent.lastSeenAt ? new Date(agent.lastSeenAt).toLocaleString() : '—'}</td>
                  <td>
                    <span className={revoked ? classes.statusInactive : classes.statusActive}>
                      {revoked ? 'מבוטל' : 'פעיל'}
                    </span>
                  </td>
                  <td>
                    <Button
                      size="xs"
                      color="red"
                      radius="md"
                      data-testid={`printer-agents-revoke-${agent.id}`}
                      onClick={() => void handleRevoke(agent.id)}
                      disabled={revoked}
                    >
                      בטל
                    </Button>
                  </td>
                </tr>
              );
            })}
            {agents.length === 0 && !isLoading && (
              <tr>
                <td colSpan={5} className={classes.empty}>
                  אין חיבורי מדפסת עדיין. צור טוקן כדי לאשר EXE שולחני.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
